/**
 * Client-side image processing utilities.
 * All processing happens on-device to minimize server costs.
 */

export interface ThumbnailOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number // 0-1, for JPEG compression
}

const DEFAULT_THUMBNAIL_OPTIONS: Required<ThumbnailOptions> = {
  maxWidth: 400,
  maxHeight: 400,
  quality: 0.8,
}

/**
 * Generate a thumbnail from an image file using Canvas API.
 * Maintains aspect ratio and fits within maxWidth/maxHeight bounds.
 * Returns a Blob of the resized image in JPEG format.
 */
export async function generateThumbnail(
  file: File,
  options: ThumbnailOptions = {}
): Promise<Blob | null> {
  // Only process images
  if (!file.type.startsWith('image/')) {
    return null
  }

  const opts = { ...DEFAULT_THUMBNAIL_OPTIONS, ...options }

  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img
      const aspectRatio = width / height

      if (width > opts.maxWidth) {
        width = opts.maxWidth
        height = width / aspectRatio
      }

      if (height > opts.maxHeight) {
        height = opts.maxHeight
        width = height * aspectRatio
      }

      // Skip if image is already small enough
      if (img.width <= opts.maxWidth && img.height <= opts.maxHeight) {
        // Still compress to JPEG for consistency
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(null)
          return
        }
        ctx.drawImage(img, 0, 0)
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          opts.quality
        )
        return
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(width)
      canvas.height = Math.round(height)

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      // Convert to JPEG blob
      canvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        opts.quality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }

    img.src = url
  })
}

/**
 * Get the thumbnail filename from the original filename.
 */
export function getThumbnailFilename(originalFilename: string): string {
  const lastDot = originalFilename.lastIndexOf('.')
  const baseName = lastDot > 0 ? originalFilename.slice(0, lastDot) : originalFilename
  return `${baseName}_thumb.jpg`
}

/**
 * Generate a thumbnail from an image URL (for backfilling existing media).
 * Downloads the image, resizes it client-side, and returns a Blob.
 */
export async function generateThumbnailFromUrl(
  imageUrl: string,
  options: ThumbnailOptions = {}
): Promise<Blob | null> {
  const opts = { ...DEFAULT_THUMBNAIL_OPTIONS, ...options }

  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Required for Firebase Storage URLs

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img
      const aspectRatio = width / height

      if (width > opts.maxWidth) {
        width = opts.maxWidth
        height = width / aspectRatio
      }

      if (height > opts.maxHeight) {
        height = opts.maxHeight
        width = height * aspectRatio
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(width)
      canvas.height = Math.round(height)

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      // Convert to JPEG blob
      canvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        opts.quality
      )
    }

    img.onerror = () => {
      resolve(null)
    }

    img.src = imageUrl
  })
}
